import { useCallback, useEffect, useRef, useState } from "react";
import { getKpis } from "../kpiGridApi";
import { normalizeRow } from "./helpers";

export const PAGE_SIZE = 200;

/**
 * One page of saved KPI rows, for the current financial year and filters.
 *
 * Filtering and paging are the server's job here rather than the client's: a
 * financial year holds roughly 2,700 active KPIs, so filtering a loaded page
 * would quietly search a fraction of them and report nothing found. `total`
 * comes back with every page so the grid can say what it is showing.
 *
 * - `loading` is true only for blocking loads (first load, or a change of
 *   year, module, filter or page); `refreshing` covers the background reload
 *   after a save so rows keep focus and scroll position.
 * - Responses carry a fetch id and a stale one is dropped, so changing filters
 *   twice quickly cannot leave the previous result on screen.
 * - `error` is surfaced with `reload` rather than rendering an empty grid,
 *   which would read as "there are no KPIs here".
 *
 * @param {{ moduleIds: string[], financialYearIds: string[], questionTypes: string[], search: string, includeInactive?: boolean, offset: number }} args
 */
export function useKpiRows({ moduleIds, financialYearIds, questionTypes, search, includeInactive = false, offset }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  const typeKey = questionTypes.join(",");
  const moduleKey = moduleIds.join(",");
  const yearKey = financialYearIds.join(",");

  const load = useCallback(
    async ({ background = false } = {}) => {
      if (!yearKey) {
        setLoading(false);
        return;
      }
      const fetchId = ++fetchIdRef.current;
      const isCurrent = () => fetchId === fetchIdRef.current;

      if (background) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const res = await getKpis({
          financialYearIds: yearKey.split(","),
          moduleIds: moduleKey ? moduleKey.split(",") : [],
          questionTypes: typeKey ? typeKey.split(",") : [],
          search,
          includeInactive,
          limit: PAGE_SIZE,
          offset,
        });
        if (!isCurrent()) return;

        const payload = res?.data?.data;
        if (!payload) {
          setError(res?.data?.error || "Could not load KPIs");
          return;
        }
        setRows((payload.rows || []).map(normalizeRow));
        setTotal(payload.total ?? 0);
      } catch (e) {
        if (!isCurrent()) return;
        setError(e?.data?.error || e?.message || "Could not load KPIs");
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [moduleKey, yearKey, typeKey, search, includeInactive, offset],
  );

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load({ background: true }), [load]);

  return { rows, total, loading, refreshing, error, refresh, reload: load };
}
